/**
 * NotificationsSheet — A2.1a touch-tier presentation (direction §7).
 *
 * On touch tiers (≤1023px) the bell opens this modal bottom-sheet instead
 * of the bell-anchored `<NotifDropdown>`. The sheet dims the page with a
 * scrim (the page does NOT move), rises from the bottom with a grab
 * handle, and puts the list + "Mark all read" in the thumb zone.
 *
 * Same rows, same payload, same actions as the dropdown — only the
 * container differs (the notification payload + 5-key allowlist are
 * LOCKED). Reuses `<NotifRow>`; pointer tiers keep `<NotifDropdown>`.
 *
 * Lives beside its siblings (NotifDropdown / NotifRow / BellTrigger)
 * rather than the handoff's `today/` path — the bell is global chrome,
 * not today-specific, and this parallels NotifDropdown 1:1.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NotifRow } from './NotifRow';
import { type Notification } from '../../hooks/useNotifications';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import { lockPageScroll } from '../../lib/pageScrollLock';
import styles from './NotificationsSheet.module.css';

interface NotificationsSheetProps {
  notifications: Notification[];
  unreadCount: number;
  onMarkAsRead: (id: string) => Promise<void>;
  onMarkAllAsRead: () => Promise<void>;
  onClose: () => void;
  /** Mirrors NotifDropdown — an ai_escalation row hands off to the modal. */
  onEscalationClick?: (notification: Notification) => void;
}

export function NotificationsSheet({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  onEscalationClick,
}: NotificationsSheetProps): JSX.Element {
  const iconsOn = useIconSystem();
  // Esc closes; lock body scroll so the page behind the scrim doesn't move.
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    const unlockPageScroll = lockPageScroll();
    return () => {
      document.removeEventListener('keydown', handleEsc);
      unlockPageScroll();
    };
  }, [onClose]);

  const hasUnread = unreadCount > 0;
  const isEmpty = notifications.length === 0;

  // B-33 — this scrim/sheet is position: fixed and is rendered by the bell
  // trigger, which lives inside the sticky TopBar <header>. Portal it to
  // document.body so it is not a descendant of the bar: the bar now carries
  // transform: translateZ(0) for a stable compositor layer, and a transformed
  // ancestor would otherwise become this fixed sheet's containing block and
  // strand it inside the 57px bar. Geometry is viewport-fixed either way.
  return createPortal(
    <div
      className={styles.scrim}
      // Tapping the scrim (but not the sheet) closes it.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={styles.sheet} role="dialog" aria-modal="true" aria-label="Notifications">
        <div className={styles.handle} aria-hidden="true" />
        <div className={styles.header}>
          <h4 className={styles.title}>Notifications</h4>
          {hasUnread ? (
            <button type="button" className={styles.markAll} onClick={() => onMarkAllAsRead()}>
              Mark all read
            </button>
          ) : null}
        </div>
        <div className={styles.list}>
          {isEmpty ? (
            <div className={styles.empty}>
              {/* A2 — `spot.empty-notify` @48 ("All quiet"). */}
              <span className={styles.emptyGlyph} aria-hidden="true">
                {iconsOn ? <Icon name="spot.empty-notify" size={48} /> : '🔔'}
              </span>
              <p className={styles.emptyTitle}>Caught up</p>
              <p className={styles.emptySub}>We'll ping you when something happens.</p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotifRow
                key={n.id}
                notification={n}
                onActivate={(id) => {
                  void onMarkAsRead(id);
                  onClose();
                }}
                onEscalationClick={onEscalationClick}
              />
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
